import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth-store'
import { apiClient, ApiClientError } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CheckCircle, XCircle, Clock, Loader2, Brain } from 'lucide-react'
import { BackgroundWrapper } from '@/components/ui/background-wrapper'

type VerificationStatus =
  | 'loading'
  | 'success'
  | 'invalid'
  | 'expired'
  | 'error'

interface VerificationResponse {
  success: boolean
  message: string
}

function VerifyEmailPage() {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const [status, setStatus] = useState<VerificationStatus>('loading')
  const [message, setMessage] = useState('')

  // Get token from URL params using URLSearchParams
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const tokenParam = urlParams.get('token')
    setToken(tokenParam)
  }, [])

  useEffect(() => {
    const verifyToken = async () => {
      if (token === null) {
        return // Still loading token from URL
      }

      if (token === '') {
        setStatus('error')
        setMessage('No verification token provided')
        return
      }

      try {
        const response: VerificationResponse =
          await apiClient.verifyEmail(token)
        setStatus('success')
        setMessage(response.message)
      } catch (error) {
        if (error instanceof ApiClientError) {
          switch (error.code) {
            case 'INVALID_TOKEN':
              setStatus('invalid')
              setMessage(error.message)
              break
            case 'TOKEN_EXPIRED':
              setStatus('expired')
              setMessage(error.message)
              break
            default:
              setStatus('error')
              setMessage(
                error.message || 'An error occurred during verification'
              )
          }
        } else {
          setStatus('error')
          setMessage('An unexpected error occurred')
        }
      }
    }

    verifyToken()
  }, [token])

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-500" />
      case 'expired':
        return <Clock className="h-12 w-12 text-orange-500" />
      case 'invalid':
      case 'error':
        return <XCircle className="h-12 w-12 text-red-500" />
      default:
        return null
    }
  }

  const getStatusTitle = () => {
    switch (status) {
      case 'loading':
        return 'Verifying your email...'
      case 'success':
        return 'Email verified successfully!'
      case 'expired':
        return 'Verification link expired'
      case 'invalid':
        return 'Invalid verification link'
      case 'error':
        return 'Verification failed'
      default:
        return ''
    }
  }

  const handleGoToDashboard = () => {
    navigate({ to: '/dashboard' })
  }

  return (
    <BackgroundWrapper>
      <div className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <Card className="overflow-hidden bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center space-y-6">
                {/* Logo */}
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                    <Brain className="h-5 w-5 text-black" />
                  </div>
                  <span className="text-lg font-semibold">ThoughtFabric</span>
                </div>

                {/* Status Icon */}
                {getStatusIcon()}

                {/* Title */}
                <h1 className="text-2xl font-bold">{getStatusTitle()}</h1>

                {/* Message */}
                <p className="text-sm text-muted-foreground text-balance">
                  {message}
                </p>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3 w-full">
                  {status === 'success' && (isAuthenticated || user) && (
                    <Button
                      onClick={handleGoToDashboard}
                      className="w-full bg-black hover:bg-black/90 text-white"
                    >
                      Go to Dashboard
                    </Button>
                  )}

                  {(status === 'invalid' ||
                    status === 'expired' ||
                    status === 'error') && (
                    <>
                      <Button
                        asChild
                        className="w-full bg-black hover:bg-black/90 text-white"
                      >
                        <Link to="/auth/register">Create Account</Link>
                      </Button>
                      <Button asChild variant="outline" className="w-full">
                        <Link to="/auth/login">Sign In</Link>
                      </Button>
                    </>
                  )}

                  {status === 'success' && !isAuthenticated && !user && (
                    <Button
                      asChild
                      className="w-full bg-black hover:bg-black/90 text-white"
                    >
                      <Link to="/auth/login">Sign In to Continue</Link>
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center text-xs text-balance text-gray-400 mt-6">
            By continuing, you agree to our{' '}
            <a
              href="https://thoughtfabric.ai/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-white transition-colors"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://thoughtfabric.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-white transition-colors"
            >
              Privacy Policy
            </a>
            .
          </div>
        </div>
      </div>
    </BackgroundWrapper>
  )
}

export const Route = createFileRoute('/verify-email/')({
  component: VerifyEmailPage,
})
